import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { CalendarClock, Check, History, Plus, RotateCcw, ShieldOff } from "lucide-react";
import { toast } from "sonner";

import { api } from "$convex/convexApi";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { InternalMetricCard } from "@/components/internal/shared/InternalMetricCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { useUsersForImpersonation } from "@/hooks/useUsers";
import { buildCustomerComboboxOptions } from "@/lib/customer-combobox";
import { formatDate, timestampToLocalDateInputString } from "@/lib/format";
import { cn } from "@/lib/utils";

const DAY_MS = 24 * 60 * 60 * 1000;

type FollowUpBucket = "active" | "backlog";
type ResolutionMode = "complete" | "snooze";
type SnoozePreset = "tomorrow" | "3_business_days" | "1_week" | "2_weeks" | "custom";

function addBusinessDays(timestamp: number, days: number): number {
  let date = new Date(timestamp);
  let added = 0;
  while (added < days) {
    date = new Date(date.getTime() + DAY_MS);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return date.getTime();
}

function resolveSnoozeUntil(preset: SnoozePreset, customDate: string): number | null {
  const now = Date.now();
  if (preset === "tomorrow") return addBusinessDays(now, 1);
  if (preset === "3_business_days") return addBusinessDays(now, 3);
  if (preset === "1_week") return now + 7 * DAY_MS;
  if (preset === "2_weeks") return now + 14 * DAY_MS;
  if (!customDate) return null;
  const parsed = new Date(`${customDate}T12:00:00`).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function formatDueDate(value: number): string {
  return formatDate(value, { month: "short", day: "numeric", year: "numeric" });
}

function relativeDueTone(followUp: any): string {
  if (followUp.bucket === "backlog") {
    return "border-zinc-300 bg-zinc-100 text-zinc-900 dark:border-zinc-700/60 dark:bg-zinc-900/40 dark:text-zinc-200";
  }
  if (followUp.dueAt < Date.now()) {
    return "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200";
  }
  return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
}

function reasonLabel(value: string): string {
  if (value === "new_account") return "New account";
  if (value === "sample_delivered") return "Sample delivered";
  if (value === "first_delivered_order") return "First delivered order";
  if (value === "manual") return "Manual";
  return value;
}

function contactLabel(value: string): string {
  return value === "call" ? "Call" : "Email";
}

function FollowUpMetricsSkeleton() {
  return (
    <section className="grid gap-4 md:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <Card key={index}>
          <CardHeader className="pb-2">
            <Skeleton className="h-4 w-24" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-8 w-12" />
            <Skeleton className="mt-2 h-3 w-32" />
          </CardContent>
        </Card>
      ))}
    </section>
  );
}

function RecentCompletedSkeleton() {
  return (
    <section className="space-y-3">
      <Skeleton className="h-5 w-32" />
      <div className="grid gap-2 md:grid-cols-2">
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className="rounded-md border px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <Skeleton className="mt-2 h-3 w-48" />
          </div>
        ))}
      </div>
    </section>
  );
}

function FollowUpActions({
  followUp,
  onResolve,
  onSuppress,
}: {
  followUp: any;
  onResolve: (followUp: any, mode: ResolutionMode) => void;
  onSuppress: (followUp: any, enabled: boolean) => void;
}) {
  const suppressed = followUp.customer?.doNotFollowUp === true;
  return (
    <div className="flex flex-wrap items-center gap-1">
      <Button size="sm" className="h-7 px-2 text-xs" onClick={() => onResolve(followUp, "complete")}>
        <Check className="mr-1 h-3 w-3" />
        Complete
      </Button>
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onResolve(followUp, "snooze")}>
        <CalendarClock className="mr-1 h-3 w-3" />
        Snooze
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs"
        onClick={() => onSuppress(followUp, !suppressed)}
      >
        {suppressed ? <RotateCcw className="mr-1 h-3 w-3" /> : <ShieldOff className="mr-1 h-3 w-3" />}
        {suppressed ? "Allow" : "Do not"}
      </Button>
    </div>
  );
}

function useColumns({
  onResolve,
  onSuppress,
}: {
  onResolve: (followUp: any, mode: ResolutionMode) => void;
  onSuppress: (followUp: any, enabled: boolean) => void;
}): ColumnDef<any>[] {
  return React.useMemo(
    () => [
      {
        id: "dueAt",
        accessorKey: "dueAt",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Due" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={relativeDueTone(row.original)}>
            {row.original.bucket === "backlog" ? "Backlog" : formatDueDate(row.original.dueAt)}
          </Badge>
        ),
        meta: { label: "Due", variant: "dateRange" as const },
        enableColumnFilter: true,
      },
      {
        id: "customer",
        accessorFn: (row) => row.customerName,
        header: ({ column }) => <DataTableColumnHeader column={column} label="Customer" />,
        cell: ({ row }) => (
          <div className="min-w-[180px]">
            <Link
              to={`/sales/crm/people?userId=${encodeURIComponent(String(row.original.customerId))}`}
              className="text-sm font-medium underline-offset-2 hover:underline"
            >
              {row.original.customerName}
            </Link>
            <p className="text-xs text-muted-foreground">{row.original.customer?.email ?? "N/A"}</p>
          </div>
        ),
        meta: { label: "Customer", placeholder: "Search customer...", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "reasons",
        accessorFn: (row) => row.reasons?.map((reason: any) => reason.type).join(" "),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Reasons" />,
        cell: ({ row }) => (
          <div className="flex max-w-[260px] flex-wrap gap-1">
            {(row.original.reasons ?? []).map((reason: any, index: number) => (
              <Badge key={`${reason.type}-${index}`} variant="outline" className="border-sky-300 bg-sky-100 text-sky-900">
                {reasonLabel(reason.type)}
              </Badge>
            ))}
          </div>
        ),
        meta: { label: "Reasons", placeholder: "Search reason...", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "owner",
        accessorFn: (row) => row.assignedSalesRepName ?? "Unassigned",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Owner" />,
        cell: ({ row }) => row.original.assignedSalesRepName ?? "Unassigned",
        meta: { label: "Owner", placeholder: "Search owner...", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "preferredContactMethod",
        accessorKey: "preferredContactMethod",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Contact" />,
        cell: ({ row }) => contactLabel(row.original.preferredContactMethod),
        meta: {
          label: "Contact",
          variant: "select" as const,
          options: [
            { label: "Call", value: "call" },
            { label: "Email", value: "email" },
          ],
        },
        enableColumnFilter: true,
      },
      {
        id: "lastTouchedAt",
        accessorFn: (row) => row.lastTouchedAt ?? row.updatedAt,
        header: ({ column }) => <DataTableColumnHeader column={column} label="Last Touched" />,
        cell: ({ row }) => {
          const value = row.original.lastTouchedAt ?? row.original.updatedAt;
          return (
            <div>
              <p className="text-xs">{value ? formatDueDate(value) : "N/A"}</p>
              <p className="text-xs text-muted-foreground">{row.original.lastTouchedByName ?? "System"}</p>
            </div>
          );
        },
        meta: { label: "Last Touched", variant: "dateRange" as const },
        enableColumnFilter: true,
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <FollowUpActions followUp={row.original} onResolve={onResolve} onSuppress={onSuppress} />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [onResolve, onSuppress],
  );
}

export default function SalesFollowUpsPage() {
  const { authUserId } = useAuthIdentity();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get("customerId");
  const [bucket, setBucket] = React.useState<FollowUpBucket>("active");
  const [manualOpen, setManualOpen] = React.useState(false);
  const [manualCustomerId, setManualCustomerId] = React.useState(customerId ?? "");
  const [manualDate, setManualDate] = React.useState(timestampToLocalDateInputString(Date.now()));
  const [manualNote, setManualNote] = React.useState("");
  const [manualContactMethod, setManualContactMethod] = React.useState<"call" | "email" | "auto">("auto");
  const [resolution, setResolution] = React.useState<{ followUp: any; mode: ResolutionMode } | null>(null);
  const [resolutionOutcome, setResolutionOutcome] = React.useState("");
  const [resolutionNote, setResolutionNote] = React.useState("");
  const [snoozePreset, setSnoozePreset] = React.useState<SnoozePreset>("tomorrow");
  const [customSnoozeDate, setCustomSnoozeDate] = React.useState("");
  const [suppression, setSuppression] = React.useState<{ followUp: any; enabled: boolean } | null>(null);
  const [suppressionReason, setSuppressionReason] = React.useState("");

  const users = useUsersForImpersonation();
  const userOptions = React.useMemo(() => buildCustomerComboboxOptions(users as any[] | undefined), [users]);
  const dashboard = useQuery(
    (api as any).salesFollowUps.getFollowUpDashboard,
    authUserId ? { authUserId } : "skip",
  );
  const followUps = useQuery(
    (api as any).salesFollowUps.listFollowUps,
    authUserId
      ? {
          authUserId,
          ...(customerId ? { customerId: customerId as any, includeCompleted: true } : { bucket }),
        }
      : "skip",
  );
  const createManualFollowUp = useMutation((api as any).salesFollowUps.createManualFollowUp);
  const completeFollowUp = useMutation((api as any).salesFollowUps.completeFollowUp);
  const snoozeFollowUp = useMutation((api as any).salesFollowUps.snoozeFollowUp);
  const setDoNotFollowUp = useMutation((api as any).salesFollowUps.setCustomerDoNotFollowUp);
  const backfillHistoricalMissedFollowUps = useMutation((api as any).salesFollowUps.backfillHistoricalMissedFollowUps);

  React.useEffect(() => {
    if (customerId) setManualCustomerId(customerId);
  }, [customerId]);

  const onResolve = React.useCallback((followUp: any, mode: ResolutionMode) => {
    setResolution({ followUp, mode });
    setResolutionOutcome("");
    setResolutionNote("");
    setSnoozePreset("tomorrow");
    setCustomSnoozeDate("");
  }, []);

  const onSuppress = React.useCallback((followUp: any, enabled: boolean) => {
    setSuppression({ followUp, enabled });
    setSuppressionReason("");
  }, []);

  const columns = useColumns({ onResolve, onSuppress });

  const handleManualCreate = async () => {
    if (!authUserId || !manualCustomerId) return;
    const dueAt = new Date(`${manualDate}T12:00:00`).getTime();
    if (!Number.isFinite(dueAt)) {
      toast.error("Choose a due date");
      return;
    }
    try {
      await createManualFollowUp({
        authUserId,
        customerId: manualCustomerId as any,
        dueAt,
        note: manualNote,
        ...(manualContactMethod === "auto" ? {} : { preferredContactMethod: manualContactMethod }),
      });
      toast.success("Follow-up created");
      setManualOpen(false);
      setManualNote("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create follow-up");
    }
  };

  const handleResolutionSubmit = async () => {
    if (!authUserId || !resolution) return;
    // Someone else can complete the same follow-up while this dialog is
    // open; the mutation reports that as a result, not an error, so the
    // rep learns who won instead of seeing a redacted "Server Error".
    const alreadyCompletedToast = (result: any, action: string) =>
      toast.info(
        result?.completedByLabel
          ? `Already completed by ${result.completedByLabel} — ${action}`
          : `This follow-up was already completed — ${action}`,
      );
    try {
      if (resolution.mode === "complete") {
        const result = await completeFollowUp({
          authUserId,
          followUpId: resolution.followUp._id,
          outcome: resolutionOutcome,
          note: resolutionNote,
        });
        if (result?.alreadyCompleted) {
          alreadyCompletedToast(result, "your completion note was not added");
        } else {
          toast.success("Follow-up completed");
        }
      } else {
        const snoozeUntil = resolveSnoozeUntil(snoozePreset, customSnoozeDate);
        if (!snoozeUntil) {
          toast.error("Choose a snooze date");
          return;
        }
        const result = await snoozeFollowUp({
          authUserId,
          followUpId: resolution.followUp._id,
          snoozeUntil,
          outcome: resolutionOutcome,
          note: resolutionNote,
        });
        if (result?.alreadyCompleted) {
          alreadyCompletedToast(result, "nothing to snooze");
        } else {
          toast.success("Follow-up snoozed");
        }
      }
      setResolution(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update follow-up");
    }
  };

  const handleSuppressionSubmit = async () => {
    if (!authUserId || !suppression) return;
    try {
      await setDoNotFollowUp({
        authUserId,
        customerId: suppression.followUp.customerId,
        enabled: suppression.enabled,
        reason: suppressionReason,
      });
      toast.success(suppression.enabled ? "Automatic follow-ups suppressed" : "Automatic follow-ups restored");
      setSuppression(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update customer");
    }
  };

  const handleBackfill = async () => {
    if (!authUserId) return;
    try {
      const result = await backfillHistoricalMissedFollowUps({ authUserId, limit: 250 });
      toast.success(`Backfill processed ${result?.processed ?? 0} events`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Backfill failed");
    }
  };

  const rows = Array.isArray(followUps) ? followUps : [];
  const showingHistory = Boolean(customerId);
  const isDashboardLoading = dashboard === undefined;
  const isFollowUpsLoading = followUps === undefined;

  return (
    <div className="min-h-full space-y-6 pb-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sales - Follow-ups</h1>
          <p className="mt-1 text-muted-foreground">
            Customer follow-up queue for sales ownership, sample handoffs, and first-order outreach.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={handleBackfill}>
            <History className="mr-2 h-4 w-4" />
            Backfill missed
          </Button>
          <Button onClick={() => setManualOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Manual follow-up
          </Button>
        </div>
      </div>

      {isDashboardLoading ? (
        <FollowUpMetricsSkeleton />
      ) : dashboard ? (
        <section className="grid gap-4 md:grid-cols-4">
          <InternalMetricCard title="Overdue" value={String(dashboard.counts.overdue)} hint="Active queue past due" icon={CalendarClock} />
          <InternalMetricCard title="Due This Week" value={String(dashboard.counts.dueThisWeek)} hint="Active follow-ups due by Sunday" icon={CalendarClock} />
          <InternalMetricCard title="Unassigned" value={String(dashboard.counts.unassigned)} hint="No sales rep owner" icon={ShieldOff} />
          <InternalMetricCard title="Backlog" value={String(dashboard.counts.backlog)} hint="Historical missed follow-ups" icon={History} />
        </section>
      ) : null}

      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>{showingHistory ? "Customer Follow-up History" : "Queue"}</CardTitle>
              <CardDescription>
                {followUps === undefined ? "Loading follow-ups..." : `${rows.length} follow-ups`}
              </CardDescription>
            </div>
            {!showingHistory ? (
              <Tabs value={bucket} onValueChange={(value) => setBucket(value as FollowUpBucket)}>
                <TabsList>
                  <TabsTrigger value="active">Active</TabsTrigger>
                  <TabsTrigger value="backlog">Backlog</TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {isFollowUpsLoading ? (
            <DataTableSkeleton columnCount={columns.length} rowCount={8} withPagination />
          ) : (
            <InternalEntityDataTable
              data={rows}
              columns={columns}
              pageSize={25}
              initialSorting={[{ id: "dueAt", desc: false }]}
            />
          )}
        </CardContent>
      </Card>

      {isDashboardLoading ? (
        <RecentCompletedSkeleton />
      ) : dashboard?.recentCompleted?.length ? (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold">Recent completed</h2>
          <div className="grid gap-2 md:grid-cols-2">
            {dashboard.recentCompleted.map((followUp: any) => (
              <div key={followUp._id} className="rounded-md border px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-medium">{followUp.customerName}</p>
                  <Badge variant="outline" className="border-emerald-300 bg-emerald-100 text-emerald-900">
                    Completed
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {followUp.completedOutcome ?? "Outcome"} - {followUp.completedAt ? formatDueDate(followUp.completedAt) : "N/A"}
                </p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manual follow-up</DialogTitle>
            <DialogDescription>Create or merge a follow-up for a customer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Customer</Label>
              <ComboboxSelect
                value={manualCustomerId}
                onValueChange={setManualCustomerId}
                options={userOptions}
                placeholder="Select customer"
                searchPlaceholder="Search customers..."
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="manual-due-date">Due date</Label>
                <Input id="manual-due-date" type="date" value={manualDate} onChange={(event) => setManualDate(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Preferred contact</Label>
                <Select value={manualContactMethod} onValueChange={(value) => setManualContactMethod(value as typeof manualContactMethod)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="manual-note">Note</Label>
              <Textarea id="manual-note" value={manualNote} onChange={(event) => setManualNote(event.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button onClick={handleManualCreate}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(resolution)} onOpenChange={(open) => !open && setResolution(null)}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{resolution?.mode === "snooze" ? "Snooze follow-up" : "Complete follow-up"}</DialogTitle>
            <DialogDescription>{resolution?.followUp?.customerName ?? "Customer follow-up"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {resolution?.mode === "snooze" ? (
              <div className={cn("grid gap-3", snoozePreset === "custom" ? "sm:grid-cols-2" : "")}>
                <div className="space-y-2">
                  <Label>Snooze until</Label>
                  <Select value={snoozePreset} onValueChange={(value) => setSnoozePreset(value as SnoozePreset)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tomorrow">Tomorrow</SelectItem>
                      <SelectItem value="3_business_days">3 business days</SelectItem>
                      <SelectItem value="1_week">1 week</SelectItem>
                      <SelectItem value="2_weeks">2 weeks</SelectItem>
                      <SelectItem value="custom">Custom date</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {snoozePreset === "custom" ? (
                  <div className="space-y-2">
                    <Label htmlFor="custom-snooze-date">Custom date</Label>
                    <Input id="custom-snooze-date" type="date" value={customSnoozeDate} onChange={(event) => setCustomSnoozeDate(event.target.value)} />
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="resolution-outcome">Outcome</Label>
              <Input id="resolution-outcome" value={resolutionOutcome} onChange={(event) => setResolutionOutcome(event.target.value)} placeholder="Connected, left voicemail, emailed..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="resolution-note">Note</Label>
              <Textarea id="resolution-note" value={resolutionNote} onChange={(event) => setResolutionNote(event.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolution(null)}>Cancel</Button>
            <Button onClick={handleResolutionSubmit}>{resolution?.mode === "snooze" ? "Snooze" : "Complete"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(suppression)} onOpenChange={(open) => !open && setSuppression(null)}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{suppression?.enabled ? "Do not follow up" : "Allow automatic follow-ups"}</DialogTitle>
            <DialogDescription>{suppression?.followUp?.customerName ?? "Customer"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="suppression-reason">Reason</Label>
            <Textarea id="suppression-reason" value={suppressionReason} onChange={(event) => setSuppressionReason(event.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuppression(null)}>Cancel</Button>
            <Button onClick={handleSuppressionSubmit}>{suppression?.enabled ? "Suppress" : "Restore"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
